import { map } from 'lodash/fp';
import { useEffect, useState, useMemo } from 'react';
import { Prompt, withRouter } from 'react-router';
import { useLocation, useHistory } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { createSelector } from 'reselect';
import {
  IonButton, IonList, IonSpinner, IonCheckbox, IonItem, IonSegment, IonSegmentButton, IonText
} from '@ionic/react';
import Geocode from 'react-geocode';

import { userInfoSelector } from 'selectors/userSelector';
import { projectsSelector, currentProjectIDSelector } from 'selectors/projectSelector';
import ProjectActions from 'actions/ProjectActions';

import useModel from 'hooks/useModel';
import { preventDefault, getValue } from 'helpers/events';
import MagnumInput from 'components/helpers/MagnumInput';
import withProject from 'components/helpers/withProject';
import { withOnline } from 'components/helpers/online-helper';
import CountrySelect from 'components/helpers/CountrySelect';
import CoordinateSelection from 'components/project/CoordinateSelection';

Geocode.setApiKey(process.env.GOOGLE_API_KEY);

function geocodeAddress(address) {
  return Geocode.fromAddress(address).then(
    (response) => response.results[0].geometry.location,
    (_error) => ({})
  );
}

const deriveProject = (project={}, copiedName) => {
  return {
    ...project,
    name: copiedName || project.name,
    client: project.client || "",
    address: project.address || "",
    city: project.city || "",
    state: project.state || "",
    zipcode: project.zipcode || "",
    country: project.country || "US",
    isPrivate: !project.published,
    verified: project.verified !== false
  };
};

const ENDING_NUMBER = /(\s+)?\d+$/;
const copiedProjectName = (originalName, projectNames) => {
  let newName = originalName + ' copy';
  while (_.includes(projectNames, newName)) {
    const trailingNumber = _.trim(_.get(newName.match(ENDING_NUMBER), 0, '1'));
    const newVersion = parseInt(trailingNumber, 10) + 1;
    newName = newName.replace(ENDING_NUMBER, '') + ' ' + newVersion;
  }
  return newName;
}

const ADDRESS_FIELDS = ["address", "city", "state", "zipcode", "country"];
const COORDINATE_FIELDS = ["latitude", 'longitude'];
const PROJECT_FIELDS = ["name", "client", ...ADDRESS_FIELDS, ...COORDINATE_FIELDS, "schema", "data", "settings"];
const projectNamesSelector = createSelector(projectsSelector, map('name'));

export function ProjectForm({online, project:initalProject}) {
  const dispatch = useDispatch();
  const location = useLocation();
  const history = useHistory();

  const {global_editor} = useSelector(userInfoSelector);

  const copyMode = useMemo(() => {
    return !!location.pathname.match(/copy/);
  }, [location.pathname]);

  const projectNames = useSelector(projectNamesSelector);
  const [
    project, setProjectProperty, mergeProject, setProject
  ] = useModel(deriveProject(initalProject, copyMode && copiedProjectName(initalProject?.name, projectNames)));
  const {
    name, client, address, city, state, zipcode, country,
    isPrivate, verified, latitude, longitude,
  } = project;

  const [submitted, setSubmitted] = useState(false);
  const [dirty, setDirty] = useState(false);
  const [error, setError] = useState();
  const [mapKey, setMapKey] = useState('initial');

  const existingNoAddress = !zipcode && longitude;
  const [activeTab, setActiveTab] = useState(existingNoAddress ? "coordinates" : "address");
  const [dirtyLocation, setDirtyLocation] = useState(() => latitude && longitude);
  const addressPresent = _.some(_.values(_.pick(project, "state", "zipcode")));

  const currentProjectID = useSelector(currentProjectIDSelector);
  useEffect(() => {
    if (copyMode && initalProject.id === currentProjectID) {
      dispatch(ProjectActions.unsetCurrentProject());
    }
  }, [copyMode, currentProjectID]);

  const {can_publish:userCanPublish=false, is_admin:isAdmin=false} = useSelector(userInfoSelector);

  useEffect(() => {
    setProject(deriveProject(initalProject, copyMode && copiedProjectName(initalProject?.name, projectNames)));
  }, [location.pathname]);

  const set = (property, value) => {
    const isLocation = _.includes(['latitude', 'longitude'], property);
    setProjectProperty(property, value);
    setDirty(true);
    if (isLocation) setDirtyLocation(Boolean(value));
  }
  const setValue = (name) => (e) => set(name, e.target.value ?? e.target.checked);
  const selectTab = (tabName) => { setActiveTab(tabName); };
  const resetLocation = () => setDirtyLocation(false);

  const getProjectField = (f) => project[f];
  useEffect(() => {
    const fullAddress = _.join(_.compact(_.map(ADDRESS_FIELDS, getProjectField)), ", ");
    const userProvidedLocation = (project.longitude && project.latitude) && dirtyLocation;
    const addressPresent = fullAddress.length > 8;

    if (addressPresent && !userProvidedLocation) {
      geocodeAddress(fullAddress).then(({lat, lng}) => {
        setMapKey(fullAddress);
        mergeProject({
          latitude: _.round(lat, 8),
          longitude: _.round(lng, 8),
        });
        setDirtyLocation(false);
      });
    }
  }, [..._.map(ADDRESS_FIELDS, getProjectField), dirtyLocation]);

  const persistProject = async () => {
    const {id} = project;
    setSubmitted(true);
    setError(null);

    const published =  userCanPublish ? !project.isPrivate : undefined;
    const verified =  isAdmin ? project.verified : undefined;

    let errors, newProjectId;

    if (!id || copyMode) {
      const newProject = await dispatch(ProjectActions.addProject({
        ..._.pick(project, PROJECT_FIELDS), published, verified, owned: true
      }));

      errors = newProject.errors;
      newProjectId = !errors && newProject.id;
    } else {
      const updatedProject = await dispatch(ProjectActions.updateProjectSettings({
        ..._.pick(project, PROJECT_FIELDS), published, verified, id
      }, project));
      errors = updatedProject.errors;
    }

    if (errors) {
      setError(errors);
    } else {
      setDirty(false);
    }

    if (newProjectId) {
      history.push(`/projects/${newProjectId}/loads`);
      return;
    }
    setSubmitted(false);
  };

  const readonly = !copyMode && project.id && !project.owned && !global_editor;
  const currentPage = !!location.pathname.match(new RegExp(`(${project.id}/(settings|copy)|new)`));

  return (
    <div className="narrow-container">
      <Prompt when={dirty && !submitted && currentPage} message="You have unsaved changes! Do you want to continue without saving?" />
      {error && <p className="alert error">{error}</p>}
      <form className="list" onSubmit={preventDefault(persistProject)} disabled={submitted}>
        <IonList>
          <IonItem>
            <MagnumInput value={name} onChange={setValue('name')} disabled={readonly}>
              <div slot="label">Project Name<IonText color="danger">*</IonText></div>
            </MagnumInput>
          </IonItem>
          <IonItem>
            <MagnumInput label="Client" value={client} onChange={setValue('client')} disabled={readonly}/>
          </IonItem>
          <br/>
          <IonSegment value={activeTab} onIonChange={preventDefault(getValue(selectTab))} mode="md">
            <IonSegmentButton value="address">Address</IonSegmentButton>
            <IonSegmentButton value="coordinates">Coordinates</IonSegmentButton>
          </IonSegment>

          <div style={{display: activeTab !== "address" ? 'none' : null}}>
            <IonItem>
              <MagnumInput label="Address" value={address} onChange={setValue('address')} disabled={readonly}/>
            </IonItem>

            <IonItem>
              <MagnumInput label="City" value={city} onChange={setValue('city')} disabled={readonly}/>
            </IonItem>

            <IonItem>
              <MagnumInput label="State/Province" value={state} onChange={setValue('state')}  disabled={readonly}/>
            </IonItem>

            <IonItem required={!readonly && !longitude && !latitude}>
              <MagnumInput label="Postal Code" value={zipcode} onChange={setValue('zipcode')} disabled={readonly}/>
            </IonItem>

            <IonItem>
              <CountrySelect value={country || 'US'} onChange={setValue('country')} disabled={readonly} labelPlacement="floating"/>
            </IonItem>
          </div>

          <div style={{display: activeTab !== "coordinates" ? 'none' : null}}>
            <CoordinateSelection key={mapKey} location={{latitude, longitude}} onChange={set} readonly={readonly}/>
            {!readonly && online && (
              <IonItem color="tertiary">
                  {latitude && longitude ? (
                    "Drag the map marker to change the project's location."
                  ) : (
                    "Click the project's location on the map to set the latitude and longitude."
                  )}
                {dirtyLocation && addressPresent && (
                  <IonButton slot="end" color="light" onClick={resetLocation}>Use Address</IonButton>
                )}
              </IonItem>
            )}
            <IonItem>
              <MagnumInput label="Latitude" value={latitude || ''} disabled={readonly} onChange={setValue('latitude')}/>
            </IonItem>

            <IonItem>
              <MagnumInput label="Longitude" value={longitude || ''} disabled={readonly} onChange={setValue('longitude')}/>
            </IonItem>
          </div>


          {userCanPublish && !readonly && (
            <IonItem>
              <IonCheckbox color="primary"
                checked={isPrivate}
                onIonChange={getValue(setProjectProperty('isPrivate'))}
                labelPlacement="end"
                justify="start"
              >
                Private
              </IonCheckbox>
            </IonItem>
          )}

          {isAdmin && (
            <IonItem>
              <IonCheckbox color="primary"
                checked={verified}
                onIonChange={getValue(setProjectProperty('verified'))}
                labelPlacement="end"
                justify="start"
              >
                Verified
              </IonCheckbox>
            </IonItem>
          )}
          <IonItem lines="none">
            {!readonly && <IonText color="danger">* = required fields</IonText>}
          </IonItem>

          <div className="padding">
            {(isAdmin || !readonly) && (
              <IonButton expand="block" type="submit" disabled={submitted}>
                {submitted && <IonSpinner name="lines-sharp-small"/>}
                SAVE {copyMode && 'COPY'}
              </IonButton>
            )}
          </div>
        </IonList>
      </form>
    </div>
  );
}

export default withProject(withOnline(withRouter(ProjectForm)));
